import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, AccordionSummary, Alert, LinearProgress, List, ListItem } from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { MonitorButton } from "./MonitorControls";
import { api } from "../utils/helpers";
import { stageLabel } from "../utils/stages";

const ENDPOINT = "/api/cicd/transport/promotions/history";
const STATUSES = new Set(["completed", "failed", "running", "queued", "unknown", "snapshot"]);

function History({ spaceId, candidateId, requestId, packageId }) {
  const { t, i18n } = useTranslation("freigabe");
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const serial = useRef(0);
  const retryOffset = useRef(0);
  useEffect(() => () => { serial.current += 1; }, []);
  const read = async offset => {
    const version = ++serial.current;
    retryOffset.current = offset;
    setLoading(true); setError(null);
    const query = new URLSearchParams({ spaceId, limit: "20", offset: String(offset),
      ...(requestId ? { requestId, packageId } : { candidateId }) });
    try {
      const result = await api(`${ENDPOINT}?${query}`);
      if (!Array.isArray(result?.items) || !Number.isInteger(result.total) || result.total < 0
          || result.offset !== offset || typeof result.hasMore !== "boolean"
          || (result.hasMore && !result.items.length)
          || result.items.some(item => !item.runId || !Array.isArray(item.artifactIds))) throw new Error("Invalid history response");
      if (version !== serial.current) return;
      setData(previous => ({ ...result, nextOffset: result.offset + result.items.length, items: offset === 0 ? result.items
        : [...new Map([...(previous?.items || []), ...result.items].map(item => [item.runId, item])).values()] }));
    } catch (failure) {
      if (version === serial.current) setError(failure?.code === "HISTORY_ANCHOR_CHANGED" ? "historyChanged" : "transportHistoryError");
    } finally { if (version === serial.current) setLoading(false); }
  };
  const date = value => Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString(i18n.language.startsWith("en") ? "en-GB" : "de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    }) : t("promotion.historyDateUnknown");
  return <Accordion className="promotion-accordion promotion-transport-history" expanded={expanded}
    onChange={(_, open) => { setExpanded(open); if (open && !data && !loading) read(0); }}>
    <AccordionSummary expandIcon={<ExpandMoreRounded/>}>{t("promotion.transportHistory")}</AccordionSummary>
    <AccordionDetails>
      <p className="promotion-muted">{t("promotion.transportHistoryHint")}</p>
      {loading && <LinearProgress aria-label={t("promotion.historyLoading")}/>}
      {error && <Alert severity="error">{t(`promotion.${error}`)}</Alert>}
      {data && <>
        <p className="promotion-history-count">{t("promotion.historyCount", { count: data.items.length, total: data.total })}</p>
        {data.items.length === 0 && <p>{t("promotion.transportHistoryEmpty")}</p>}
        <List className="promotion-history-list" disablePadding>{data.items.map(item => {
          const status = STATUSES.has(item.status) ? item.status : "unknown";
          return <ListItem className="promotion-history-entry" key={item.runId} disableGutters>
            <div className="promotion-history-meta">
              <time dateTime={item.at || undefined}>{date(item.at)}</time>
              <span className={`promotion-status promotion-status--${status}`}><span aria-hidden="true"/>
                {t(status === "snapshot" ? "promotion.historySnapshot" : `promotion.status.${status}`)}</span>
            </div>
            {(item.sourceInstanceName || item.sourceStage || item.targetInstanceName || item.targetStage) &&
              <p className="promotion-history-route">{item.sourceInstanceName || stageLabel(item.sourceStage) || t("promotion.historySourceUnknown")}
                {status !== "snapshot" && <><span aria-hidden="true"> ››› </span><span className="promotion-sr-only">{t("promotion.historyTo")} </span>
                  {item.targetInstanceName || stageLabel(item.targetStage) || t("promotion.instanceMissing")}</>}</p>}
            {item.outcome && <p>{t("promotion.outcome", { ...item.outcome, edge: t(`promotion.edge.${item.outcome.edge}`) })}</p>}
            <p className="promotion-history-artifacts">{item.artifactIds.join(", ")}</p>
            {item.usedAsEvidence && <p className="promotion-history-evidence">{t("promotion.historyEvidence", {
              artifacts: (item.evidenceArtifactIds || []).join(", "),
            })}</p>}
          </ListItem>;
        })}</List>
      </>}
      <div className="promotion-actions">
        {error && error !== "historyChanged" && <MonitorButton disabled={loading} onClick={() => read(retryOffset.current)}>{t("promotion.historyRetry")}</MonitorButton>}
        {!error && data?.hasMore && <MonitorButton disabled={loading} onClick={() => read(data.nextOffset)}>{t("promotion.moreTransports")}</MonitorButton>}
      </div>
    </AccordionDetails>
  </Accordion>;
}

export default function PromotionTransportHistory(props) {
  if (!props.spaceId || (!props.candidateId && !(props.requestId && props.packageId))) return null;
  return <History key={`${props.spaceId}:${props.requestId || props.candidateId}:${props.packageId || ""}`} {...props}/>;
}
